import React, { useEffect, useState } from 'react'
import Card from 'react-bootstrap/Card'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import ActionButton from 'shared/buttons/action-button'
import { faPlus, faLayerGroup } from '@fortawesome/free-solid-svg-icons'
import {
  useAccountGroupings,
  defaultGrouping
} from 'api/hooks/use-account-groupings'
import AccountGroupTable from './table'
import GroupModal from './group-modal'
import GroupingModal from './grouping-modal'

// Account Groups roll up account codes (or whole accounts) under a code and name
// of their own — stored apart, the way an account code's are — so charts, pivot
// tables, filters, and exports can report on them as one bucket. Ungrouped codes
// keep reporting individually.
//
// Shows one grouping at a time, as reporting does; mixing them would suggest a code can only
// be claimed once overall.
//
// Rendered as the Settings > Reporting > Account Groups tab, so it carries no
// app chrome of its own — the Settings shell supplies it (see settings/panels).
const AccountGroups = () => {
  const [showModal, setShowModal] = useState(false)
  const [showGroupings, setShowGroupings] = useState(false)
  const [editing, setEditing] = useState(null)
  const [viewOnly, setViewOnly] = useState(false)
  const [groupingId, setGroupingId] = useState(null)

  const { data: groupings } = useAccountGroupings({})

  // Follow the list when the selected grouping is deleted: a stale id empties the table.
  useEffect(() => {
    if (!groupings?.length) return
    if (groupings.some((grouping) => grouping.id === groupingId)) return

    setGroupingId(defaultGrouping(groupings)?.id || null)
  }, [groupings, groupingId])

  const openCreate = () => {
    setEditing(null)
    setViewOnly(false)
    setShowModal(true)
  }

  const openEdit = (group) => {
    setEditing(group)
    setViewOnly(false)
    setShowModal(true)
  }

  const openView = (group) => {
    setEditing(group)
    setViewOnly(true)
    setShowModal(true)
  }

  // The page owns the selection, but the control renders in the table's filter row so
  // grouping, search and segment read as one toolbar. A native select can't hold a badge, so
  // the default is marked on the option.
  const groupingSelect = (
    <Form.Select
      aria-label="Account grouping"
      data-testid="grouping-select"
      value={groupingId || ''}
      disabled={!groupingId}
      onChange={(e) => setGroupingId(e.target.value)}
    >
      {/*
        On the first render the groupings haven't loaded and the effect above hasn't seeded the
        selection, so the value is '' and matches no option — React warns, and the control shows
        the first grouping while the table below is still showing nothing. A placeholder gives
        that moment an option of its own, and it disappears as soon as there is a real selection.
      */}
      {!groupingId && <option value="">Loading…</option>}
      {(groupings || []).map((grouping) => (
        <option key={grouping.id} value={grouping.id}>
          {grouping.is_default ? `${grouping.name} (Default)` : grouping.name}
        </option>
      ))}
    </Form.Select>
  )

  return (
    <Row>
      <Col>
        <Card>
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Account Groups</h5>
              <div className="d-flex gap-2 flex-shrink-0">
                <ActionButton
                  label="Groupings"
                  icon={faLayerGroup}
                  variant="light"
                  className="text-primary"
                  onClick={() => setShowGroupings(true)}
                />
                <ActionButton
                  label="Add Group"
                  icon={faPlus}
                  variant="primary"
                  disabled={!groupingId}
                  onClick={openCreate}
                />
              </div>
            </div>

            <p className="text-muted small mb-0 mt-2">
              Roll up account codes under one code and name — group objects 0110,
              0120, and 0130 as “100 Salaries &amp; Benefits” and report on the
              group anywhere the Object Group column is available.
            </p>

            <hr />
            <AccountGroupTable
              groupingId={groupingId}
              toolbarStart={groupingSelect}
              onView={openView}
              onEdit={openEdit}
            />
          </Card.Body>
        </Card>

        <GroupModal
          show={showModal}
          group={editing}
          groupingId={groupingId}
          readOnly={viewOnly}
          onHide={() => setShowModal(false)}
        />

        <GroupingModal
          show={showGroupings}
          onHide={() => setShowGroupings(false)}
          onSelect={setGroupingId}
        />
      </Col>
    </Row>
  )
}

export default AccountGroups
